import type { OddsFormat } from "../features/livescore/types";

export type OddRow = Record<string, any>;
export const DEFAULT_ODDS_FORMAT: OddsFormat = "raw";
export const ODD_FIELD_KEYS = {
  home: ["home", "homeOdds", "home_odds", "homeWin", "up", "upOdds", "over"],
  away: ["away", "awayOdds", "away_odds", "guestWin", "down", "downOdds", "under"],
  draw: ["draw", "drawOdds", "draw_odds", "standoff"],
  line: ["handicap", "handicapValue", "handicap_value", "line", "pan", "market", "goal"],
  over: ["over", "overOdds", "over_odds", "big", "bigOdds", "up"],
  under: ["under", "underOdds", "under_odds", "small", "smallOdds", "down"],
} as const;

const numericOdd = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeOddsFormat = (value: unknown): OddsFormat => {
  const format = String(value || DEFAULT_ODDS_FORMAT).toLowerCase() as OddsFormat;
  return ["raw", "decimal", "malay", "hongkong", "indo", "indonesian"].includes(format) ? format : DEFAULT_ODDS_FORMAT;
};

export const roundOdd = (value: number): number => Number(value.toFixed(2));

/** Single display formatter for every odds UI. */
export const formatOdd = (value: unknown, format: OddsFormat = DEFAULT_ODDS_FORMAT): string => {
  if (value === undefined || value === null || value === "") return "-";
  const number = numericOdd(value);
  if (number === null) return String(value);
  // API currently supplies the value in the requested format. Keep conversion
  // centralized here so later format-specific formulas only need one change.
  switch (normalizeOddsFormat(format)) {
    case "decimal":
    case "malay":
    case "hongkong":
    case "indo":
    case "indonesian":
    case "raw":
    default:
      return String(roundOdd(number));
  }
};

export const isOddRow = (source: unknown): source is OddRow => Boolean(source && typeof source === "object" && !Array.isArray(source) && ["companyId", "company_id", "cid", "home", "homeOdds", "home_odds", "homeWin", "away", "awayOdds", "away_odds", "guestWin", "draw", "drawOdds", "standoff", "over", "overOdds", "over_odds", "under", "underOdds", "under_odds", "handicap", "handicapValue", "line", "total", "pan", "market"].some((key) => (source as OddRow)[key] !== undefined));
export const isOddTuple = (source: unknown): source is any[] => Array.isArray(source) && source.length >= 5 && Number.isFinite(Number(source[0]));
const tupleOdd = (source: any[], companyId?: string, market?: string): OddRow => {
  const base = { companyId, marketType: market, updatedAt: source[0], status: source[5], change: source[6], score: source[7] };
  if (market === "eu" || market === "euHt") return { ...base, home: source[2], homeOdds: source[2], draw: source[3], drawOdds: source[3], away: source[4], awayOdds: source[4] };
  return { ...base, home: source[2], homeOdds: source[2], up: source[2], over: source[2], overOdds: source[2], handicap: source[3], handicapValue: source[3], line: source[3], total: source[3], away: source[4], awayOdds: source[4], down: source[4], under: source[4], underOdds: source[4] };
};

export const flattenOdds = (source: unknown, companyId?: string, market?: string): OddRow[] => {
  if (!source) return [];
  if (isOddTuple(source)) return [tupleOdd(source, companyId, market)];
  if (Array.isArray(source)) return source.flatMap((entry) => flattenOdds(entry, companyId, market));
  if (isOddRow(source)) return [source];
  if (typeof source === "object") return Object.entries(source).flatMap(([key, entry]) => flattenOdds(entry, key, market));
  return [];
};

export const findCompanyOdd = (source: unknown, companyId: string, market?: string): OddRow | null => {
  const rows = flattenOdds(source, undefined, market);
  const companyRows = rows.filter((row) => String(row.companyId ?? row.company_id ?? row.cid ?? "") === companyId);
  const usable = companyRows.length ? companyRows : rows;
  return usable.sort((a, b) => Number(b.updatedAt ?? b.updateTime ?? b.time ?? 0) - Number(a.updatedAt ?? a.updateTime ?? a.time ?? 0))[0] || null;
};

export const pickOdd = (source: OddRow | null | undefined, keys: string[], fallback: unknown = "-"): unknown => {
  for (const key of keys) if (source?.[key] !== undefined && source[key] !== null && source[key] !== "") return source[key];
  return fallback;
};

export const oddsSource = (match: any, key: string): unknown => match?.[key] ?? match?.info?.[key] ?? match?.odds?.[key] ?? match?.markets?.[key];

export const oddTrend = (source: OddRow | null | undefined): "up" | "down" | "" => {
  const raw = source?.change ?? source?.trend ?? source?.changeType ?? source?.change_type;
  const value = String(raw ?? "").trim().toLowerCase();
  if (["1", "+1", "up", "increase", "increased", "rise", "rising"].includes(value)) return "up";
  if (["-1", "2", "down", "decrease", "decreased", "fall", "falling"].includes(value)) return "down";
  return "";
};

export const absoluteHandicap = (value: unknown): string | number => typeof value === "number" ? Math.abs(value) : String(value ?? "").replace(/[+-](?=\d|\.)/g, "");

export const handicapOwner = (value: unknown): "home" | "away" => /-(?:\d|\.)/.test(String(value ?? "").replace(/\s+/g, "")) ? "away" : "home";
